/**
 * ChatCardPreview component for the Indigo design system.
 *
 * A compact card used inside chat surfaces to preview a physician, location,
 * or any custom entity. Renders an icon tile on the left and a stack of
 * title / subtitle / action buttons on the right.
 *
 * Predefined types come with an icon and color palette baked in:
 *   - `physician` → sky-500 icon on sky-50 background (uses `physician-icon`).
 *   - `location`  → olive-600 icon on olive-50 background (uses `location-icon`).
 *   - `custom`    → caller supplies the icon and (optionally) colors.
 *
 * @example
 * ```tsx
 * <ChatCardPreview
 *   type="physician"
 *   title="Dr. Annette Zhu"
 *   subtitle="Pain management"
 *   actions={[
 *     { label: "Preferences", icon: <Icon name="preferences" size="s" />, onClick: ... },
 *     { label: "Schedule", icon: <Icon name="calendar" size="s" />, onClick: ... },
 *   ]}
 * />
 * ```
 */
import { clsx } from "clsx";
import { CSSProperties, forwardRef, ReactNode } from "react";
import { Button } from "../../Button/Button";
import { Icon } from "../../Icons";
import { Body } from "../../Typography/Body";
import "./ChatCardPreview.css";

export type ChatCardPreviewType = "physician" | "location" | "custom";

export interface ChatCardPreviewAction {
  /** Visible label for the action button. */
  label: string;
  /** Optional icon rendered to the left of the label. */
  icon?: ReactNode;
  /** Click handler. */
  onClick?: () => void;
  /** Optional test id for the action button. */
  "data-testid"?: string;
}

export interface ChatCardPreviewProps {
  /** Predefined preview kind. Use `"custom"` to supply your own icon/colors. */
  type: ChatCardPreviewType;
  /** Title text. Rendered as Body S. */
  title: string;
  /** Subtitle text. Rendered as Body XS in tertiary color. */
  subtitle: string;
  /** Optional list of ghost action buttons rendered under the subtitle. */
  actions?: ChatCardPreviewAction[];
  /** Custom icon node. Required when `type === "custom"`. */
  icon?: ReactNode;
  /** Foreground (icon) color for `type === "custom"`. */
  iconColor?: string;
  /** Background color of the icon tile for `type === "custom"`. */
  iconBgColor?: string;
  /** Additional CSS class applied to the root. */
  className?: string;
  /** Test id for the root element. */
  "data-testid"?: string;
}

const PREDEFINED_ICON: Record<
  Exclude<ChatCardPreviewType, "custom">,
  ReactNode
> = {
  physician: <Icon name="physician-icon" size="m" />,
  location: <Icon name="location-icon" size="m" />,
};

export const ChatCardPreview = forwardRef<HTMLDivElement, ChatCardPreviewProps>(
  (
    {
      type,
      title,
      subtitle,
      actions,
      icon,
      iconColor,
      iconBgColor,
      className,
      "data-testid": dataTestId,
    },
    ref,
  ) => {
    const isCustom = type === "custom";
    const iconNode = isCustom ? icon : PREDEFINED_ICON[type];

    const customStyle: CSSProperties | undefined = isCustom
      ? {
          ...(iconBgColor ? { background: iconBgColor } : null),
          ...(iconColor ? { color: iconColor } : null),
        }
      : undefined;

    return (
      <div
        ref={ref}
        className={clsx("indigo-chat-card-preview", className)}
        data-testid={dataTestId}
      >
        <div
          className={clsx(
            "indigo-chat-card-preview__icon-wrap",
            !isCustom && `indigo-chat-card-preview__icon-wrap--${type}`,
          )}
          style={customStyle}
        >
          {iconNode}
        </div>
        <div className="indigo-chat-card-preview__body">
          <Body as="span" size="sm" className="indigo-chat-card-preview__title">
            {title}
          </Body>
          <Body
            as="span"
            size="xs"
            className="indigo-chat-card-preview__subtitle"
          >
            {subtitle}
          </Body>
          {actions && actions.length > 0 && (
            <div className="indigo-chat-card-preview__actions">
              {actions.map((action, idx) => (
                <Button
                  key={`${action.label}-${idx}`}
                  variant="ghost"
                  size="xs"
                  icon={action.icon}
                  onClick={action.onClick}
                  data-testid={action["data-testid"]}
                >
                  {action.label}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  },
);

ChatCardPreview.displayName = "ChatCardPreview";

export default ChatCardPreview;
